"use client";

import { c as _c } from "react/compiler-runtime";
import { trackEmailComposerAction } from "../analytics";
import { useEmailComposer } from "../context";
import { NodeSelection, TextSelection } from "@tiptap/pm/state";
import { useEditorState } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { useState } from "react";
import { LuAlignCenter, LuAlignLeft, LuAlignRight, LuCheck, LuChevronDown, LuCopy, LuHeading1, LuHeading2, LuHeading3, LuList, LuListOrdered, LuTextQuote, LuTrash2, LuType } from "react-icons/lu";
import { Button } from "@/components/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cx } from "@/lib/tailwind-utils";
import { getClickedCellIndex, getClickedNonTextBlockType } from "./node-selection-drag-plugin";
import { SectionStylePanel, SectionStyleToggle } from "./section-style-panel";
function Separator() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-5 w-px bg-border" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
type BlockType = "paragraph" | "heading1" | "heading2" | "heading3" | "bulletList" | "orderedList" | "blockquote";
interface BlockTypeEntry {
  type: BlockType;
  label: string;
  icon: React.ReactNode;
}
const BLOCK_TYPES: BlockTypeEntry[] = [{
  type: "paragraph",
  label: "Text",
  icon: <LuType size={16} />
}, {
  type: "heading1",
  label: "Heading 1",
  icon: <LuHeading1 size={16} />
}, {
  type: "heading2",
  label: "Heading 2",
  icon: <LuHeading2 size={16} />
}, {
  type: "heading3",
  label: "Heading 3",
  icon: <LuHeading3 size={16} />
}, {
  type: "bulletList",
  label: "Bullet List",
  icon: <LuList size={16} />
}, {
  type: "orderedList",
  label: "Numbered List",
  icon: <LuListOrdered size={16} />
}, {
  type: "blockquote",
  label: "Quote",
  icon: <LuTextQuote size={16} />
}];
function getActiveBlockType(state: {
  heading1: boolean;
  heading2: boolean;
  heading3: boolean;
  bulletList: boolean;
  orderedList: boolean;
  blockquote: boolean;
}): BlockType {
  if (state.heading1) return "heading1";
  if (state.heading2) return "heading2";
  if (state.heading3) return "heading3";
  if (state.bulletList) return "bulletList";
  if (state.orderedList) return "orderedList";
  if (state.blockquote) return "blockquote";
  return "paragraph";
}
function getBlockLabel(type: BlockType): string {
  return BLOCK_TYPES.find(b => b.type === type)?.label ?? "Text";
}

/**
 * Bubble menu that appears when a block (row) is NodeSelected.
 * Offers block type switching, alignment, duplicate, and delete.
 */
export function BlockBubbleMenu() {
  const {
    mode,
    editor,
    bubbleMenuContainerRef
  } = useEmailComposer();
  const [blockTypeOpen, setBlockTypeOpen] = useState(false);
  // The section whose style panel is open — selecting another closes it.
  const [sectionOpenFor, setSectionOpenFor] = useState<string | null>(null);
  const state = useEditorState({
    editor,
    selector: ctx => {
      if (!ctx.editor) return null;
      const sel = ctx.editor.state.selection;
      if (!(sel instanceof NodeSelection)) return null;
      const node = sel.node;

      // Don't show for image or button (they have their own menus)
      if (node.type.name === "emailImage" || node.type.name === "emailButton") {
        return null;
      }
      let targetBlock: import("@tiptap/pm/model").Node | null = null;
      let isRowLevel = false;
      // The section (row + cell) the selection sits in, for its style panel.
      let sectionRowPos: number | null = null;
      let cellIndex = 0;
      if (node.type.name === "emailRow") {
        // Row-level selection — read from the clicked cell (not just the first)
        const clickedIdx = getClickedCellIndex();
        const cellIdx = clickedIdx < node.childCount ? clickedIdx : 0;
        sectionRowPos = sel.from;
        cellIndex = cellIdx;
        const cell = node.child(cellIdx);
        const block = cell?.firstChild;
        // The image or button that was clicked has its own menu.
        if (getClickedNonTextBlockType()) return null;
        // Don't show for rows whose only block is image or button
        if (block && cell?.childCount === 1 && (block.type.name === "emailImage" || block.type.name === "emailButton")) {
          return null;
        }
        targetBlock = block ?? null;
        isRowLevel = true;
      } else {
        // Block-level selection (individual block in multi-block cell)
        const $block = ctx.editor.state.doc.resolve(sel.from);
        for (let d = $block.depth; d > 0; d--) {
          if ($block.node(d).type.name === "emailCell") {
            sectionRowPos = $block.before(d - 1);
            cellIndex = $block.index(d - 1);
            break;
          }
        }
        const group = node.type.spec.group as string | undefined;
        if (group?.includes("emailBlock") || node.type.name === "bulletList" || node.type.name === "orderedList") {
          targetBlock = node;
        }
      }
      if (!targetBlock) return null;
      const blockName = targetBlock.type.name;
      const blockAttrs = targetBlock.attrs ?? {};
      return {
        heading1: blockName === "emailHeading" && blockAttrs.level === 1,
        heading2: blockName === "emailHeading" && blockAttrs.level === 2,
        heading3: blockName === "emailHeading" && blockAttrs.level === 3,
        bulletList: blockName === "bulletList",
        orderedList: blockName === "orderedList",
        blockquote: blockName === "emailBlockquote",
        paragraph: blockName === "emailParagraph",
        alignLeft: blockAttrs.textAlign === "left" || !blockAttrs.textAlign,
        alignCenter: blockAttrs.textAlign === "center",
        alignRight: blockAttrs.textAlign === "right",
        pos: sel.from,
        isRowLevel,
        sectionRowPos,
        cellIndex
      };
    }
  });
  if (!editor || !state || mode === "simple") return null;
  const sectionKey = `${state.sectionRowPos}:${state.cellIndex}`;
  const sectionOpen = state.sectionRowPos !== null && sectionOpenFor === sectionKey;
  const activeBlockType = getActiveBlockType(state);
  function applyBlockType(type: BlockType) {
    if (!editor) return;
    const {
      doc,
      schema
    } = editor.state;
    const pos = state!.pos;
    const node_0 = doc.nodeAt(pos);
    if (!node_0) return;
    if (state!.isRowLevel) {
      // Row-level: convert all blocks in the clicked cell
      const clickedIdx_0 = getClickedCellIndex();
      const cellIdx_0 = clickedIdx_0 < node_0.childCount ? clickedIdx_0 : 0;
      const cell_0 = node_0.child(cellIdx_0);
      if (!cell_0) return;
      let cellStart = pos + 1;
      for (let c = 0; c < cellIdx_0; c++) cellStart += node_0.child(c).nodeSize;
      if (type === "bulletList" || type === "orderedList") {
        const innerPos = cellStart + 2;
        const {
          tr
        } = editor.state;
        tr.setSelection(TextSelection.near(doc.resolve(innerPos)));
        editor.view.dispatch(tr);
        if (type === "bulletList") {
          editor.chain().focus().toggleBulletList().run();
        } else {
          editor.chain().focus().toggleOrderedList().run();
        }
      } else {
        const targetType = type === "paragraph" ? schema.nodes["emailParagraph"] : type === "blockquote" ? schema.nodes["emailBlockquote"] : schema.nodes["emailHeading"];
        const attrs = type === "heading1" ? {
          level: 1
        } : type === "heading2" ? {
          level: 2
        } : type === "heading3" ? {
          level: 3
        } : undefined;
        if (!targetType) return;
        const {
          tr: tr_0
        } = editor.state;
        let offset = cellStart + 1;
        cell_0.forEach(block_0 => {
          if (block_0.type.name === "emailParagraph" || block_0.type.name === "emailHeading" || block_0.type.name === "emailBlockquote") {
            tr_0.setNodeMarkup(offset, targetType, {
              ...block_0.attrs,
              ...attrs
            });
          }
          offset += block_0.nodeSize;
        });
        tr_0.setSelection(NodeSelection.create(tr_0.doc, pos));
        editor.view.dispatch(tr_0);
      }
    } else {
      // Block-level: convert just the selected block
      if (type === "bulletList" || type === "orderedList") {
        const {
          tr: tr_1
        } = editor.state;
        tr_1.setSelection(TextSelection.near(doc.resolve(pos + 1)));
        editor.view.dispatch(tr_1);
        if (type === "bulletList") {
          editor.chain().focus().toggleBulletList().run();
        } else {
          editor.chain().focus().toggleOrderedList().run();
        }
      } else {
        const targetType_0 = type === "paragraph" ? schema.nodes["emailParagraph"] : type === "blockquote" ? schema.nodes["emailBlockquote"] : schema.nodes["emailHeading"];
        const attrs_0 = type === "heading1" ? {
          level: 1
        } : type === "heading2" ? {
          level: 2
        } : type === "heading3" ? {
          level: 3
        } : undefined;
        if (!targetType_0) return;
        const {
          tr: tr_2
        } = editor.state;
        tr_2.setNodeMarkup(pos, targetType_0, {
          ...node_0.attrs,
          ...attrs_0
        });
        tr_2.setSelection(NodeSelection.create(tr_2.doc, pos));
        editor.view.dispatch(tr_2);
      }
    }

    // Re-select
    setTimeout(() => {
      try {
        editor.view.dispatch(editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, state!.pos)));
      } catch {
        // Position may have shifted
      }
    }, 0);
    trackEmailComposerAction("block_type_changed", "block_bubble_menu", {
      type
    });
    setBlockTypeOpen(false);
  }
  function handleAlign(alignment: string) {
    if (!editor) return;
    const {
      doc: doc_0
    } = editor.state;
    const pos_0 = state!.pos;
    const node_1 = doc_0.nodeAt(pos_0);
    if (!node_1) return;
    const {
      tr: tr_3
    } = editor.state;
    if (state!.isRowLevel) {
      const clickedIdx_1 = getClickedCellIndex();
      const cellIdx_1 = clickedIdx_1 < node_1.childCount ? clickedIdx_1 : 0;
      const cell_1 = node_1.child(cellIdx_1);
      if (!cell_1) return;
      let cellStart_0 = pos_0 + 1;
      for (let c_0 = 0; c_0 < cellIdx_1; c_0++) cellStart_0 += node_1.child(c_0).nodeSize;
      let offset_0 = cellStart_0 + 1;
      cell_1.forEach(block_1 => {
        if (block_1.attrs && "textAlign" in block_1.attrs) {
          tr_3.setNodeMarkup(offset_0, undefined, {
            ...block_1.attrs,
            textAlign: alignment
          });
        }
        offset_0 += block_1.nodeSize;
      });
      tr_3.setSelection(NodeSelection.create(tr_3.doc, pos_0));
    } else {
      if (node_1.attrs && "textAlign" in node_1.attrs) {
        tr_3.setNodeMarkup(pos_0, undefined, {
          ...node_1.attrs,
          textAlign: alignment
        });
      }
      tr_3.setSelection(NodeSelection.create(tr_3.doc, pos_0));
    }
    editor.view.dispatch(tr_3);
    trackEmailComposerAction("align_changed", "block_bubble_menu", {
      alignment
    });
  }
  function handleDuplicate() {
    if (!editor) return;
    const {
      doc: doc_1,
      tr: tr_4
    } = editor.state;
    const node_2 = doc_1.nodeAt(state!.pos);
    if (!node_2) return;
    const insertPos = state!.pos + node_2.nodeSize;
    tr_4.insert(insertPos, node_2.copy(node_2.content));
    tr_4.setSelection(NodeSelection.create(tr_4.doc, insertPos));
    editor.view.dispatch(tr_4);
    trackEmailComposerAction("block_duplicated", "block_bubble_menu");
  }
  function handleDelete() {
    if (!editor) return;
    editor.chain().focus().deleteSelection().run();
    trackEmailComposerAction("block_deleted", "block_bubble_menu");
  }
  return <BubbleMenu editor={editor} getReferencedVirtualElement={() => {
    if (!state) return null;
    const toVirtual = (el: HTMLElement) => ({
      getBoundingClientRect: () => el.getBoundingClientRect(),
      getClientRects: () => [el.getBoundingClientRect()],
      contextElement: el
    });
    if (state.isRowLevel) {
      // Get the row DOM (vanilla JS NodeView) and find the clicked cell
      const rowDom = editor.view.nodeDOM(state.pos);
      if (rowDom instanceof HTMLElement) {
        const clickedIdx_2 = getClickedCellIndex();
        const rowNode = editor.state.doc.nodeAt(state.pos);
        const cellIdx_2 = rowNode && clickedIdx_2 < rowNode.childCount ? clickedIdx_2 : 0;
        const cellDom = rowDom.children[cellIdx_2];
        if (cellDom instanceof HTMLElement) return toVirtual(cellDom);
      }
    }
    const el_0 = editor.view.nodeDOM(state.pos);
    if (el_0 instanceof HTMLElement) return toVirtual(el_0);
    return null;
  }} options={{
    autoPlacement: {
      allowedPlacements: ["top", "bottom"],
      crossAxis: true
    }
  }} appendTo={bubbleMenuContainerRef.current ?? undefined}>
      <div className="rounded-lg border border-border bg-background shadow-md">
        <div className="flex items-center gap-0.5 px-1 py-1">
          {/* Block type dropdown */}
          <Popover open={blockTypeOpen} onOpenChange={setBlockTypeOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 px-2 text-caption-strong" onMouseDown={e => e.preventDefault()}>
                {getBlockLabel(activeBlockType)}
                <LuChevronDown size={12} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" sideOffset={8} className="w-44 p-1" onOpenAutoFocus={e_0 => e_0.preventDefault()}>
              {BLOCK_TYPES.map(block_2 => <button type="button" key={block_2.type} onClick={() => applyBlockType(block_2.type)} className={cx("flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-body transition-colors", activeBlockType === block_2.type ? "bg-primary/10 text-foreground" : "text-foreground hover:bg-hover")}>
                  <span className="flex size-5 items-center justify-center text-foreground-secondary">
                    {block_2.icon}
                  </span>
                  <span className="flex-1 text-left">{block_2.label}</span>
                  {activeBlockType === block_2.type && <LuCheck size={14} className="text-primary" />}
                </button>)}
            </PopoverContent>
          </Popover>

          <Separator />

          {/* Alignment */}
          <Button type="button" variant="ghost" size="icon-sm" onMouseDown={e_1 => e_1.preventDefault()} onClick={() => handleAlign("left")} className={cx(state.alignLeft && "bg-hover")} tooltip="Align left">
            <LuAlignLeft size={15} />
          </Button>
          <Button type="button" variant="ghost" size="icon-sm" onMouseDown={e_2 => e_2.preventDefault()} onClick={() => handleAlign("center")} className={cx(state.alignCenter && "bg-hover")} tooltip="Align center">
            <LuAlignCenter size={15} />
          </Button>
          <Button type="button" variant="ghost" size="icon-sm" onMouseDown={e_3 => e_3.preventDefault()} onClick={() => handleAlign("right")} className={cx(state.alignRight && "bg-hover")} tooltip="Align right">
            <LuAlignRight size={15} />
          </Button>

          <Separator />

          {state.sectionRowPos !== null && <SectionStyleToggle open={sectionOpen} onToggle={() => setSectionOpenFor(sectionOpen ? null : sectionKey)} />}

          {/* Duplicate */}
          <Button type="button" variant="ghost" size="icon-sm" onMouseDown={e_4 => e_4.preventDefault()} onClick={handleDuplicate} tooltip="Duplicate block">
            <LuCopy size={15} />
          </Button>

          {/* Delete */}
          <Button type="button" variant="ghost" size="icon-sm" onMouseDown={e_5 => e_5.preventDefault()} onClick={handleDelete} className="text-status-error-text hover:bg-status-error-bg" tooltip="Delete block">
            <LuTrash2 size={15} />
          </Button>
        </div>
        {sectionOpen && state.sectionRowPos !== null && <SectionStylePanel rowPos={state.sectionRowPos} cellIndex={state.cellIndex} />}
      </div>
    </BubbleMenu>;
}